import { adminFetch } from './dataProvider';

/**
 * `PUT /v1/admin/crm/{source}/owner-defaults` (Task 7) — the dedicated,
 * source-agnostic write path for the two owner-fallback-chain settings
 * already read out of `crm_connections.config`: the company-wide default
 * owner and the per-conference `conferenceId -> ownerId` override map.
 *
 * Deliberately NOT folded into `crmConnections.ts`'s `saveCrmConnection`
 * (`PUT /v1/admin/crm/connections/{id}`) — that endpoint is connection
 * CREDENTIALS: it 409s outright for a non-Salesforce row, and even for
 * Salesforce its config schema has no passthrough for these two keys, so a
 * client sending them through that PUT would have them silently dropped
 * before the handler's own merge logic ever ran. This route describes
 * tenant MERGE CONFIGURATION instead, which applies to whichever of the
 * three sources is actually connected. See viq-backend
 * `apps/customer-portal/src/handlers/crmOwnerDefaults.ts`.
 *
 * Wire contract (mirrors the backend's `CrmOwnerDefaultsInputSchema`):
 *  - `defaultOwnerId`: omit the field entirely = leave the stored value
 *    unchanged, `null` = clear it, a non-empty string = set it.
 *  - `conferenceDefault`, when present, touches exactly ONE entry of the
 *    `defaultOwnerByConference` map the same way (`ownerId: null` clears
 *    just that entry; every other conference's entry is left alone).
 *  - At least one of the two fields is required — the backend 422s an empty
 *    body rather than accepting a silent no-op PUT.
 *
 * The backend merges this into the connection's existing `config` (it never
 * replaces the object wholesale) and deletes a cleared key outright rather
 * than storing a JSON `null` or an empty string — so this client only ever
 * needs to send the narrow diff, never the whole config.
 */
export interface CrmOwnerDefaultsInput {
  defaultOwnerId?: string | null;
  conferenceDefault?: { conferenceId: string; ownerId: string | null };
}

/** What the backend echoes back — the merged settings, not the full config. */
export interface CrmOwnerDefaultsResult {
  defaultOwnerId?: string;
  defaultOwnerByConference?: Record<string, string>;
}

export const saveCrmOwnerDefaults = (
  source: string,
  body: CrmOwnerDefaultsInput,
): Promise<CrmOwnerDefaultsResult> =>
  adminFetch<CrmOwnerDefaultsResult>(`/v1/admin/crm/${source}/owner-defaults`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
